import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import type { ApiError } from "@/lib/api/unwrap";
import { ACCOUNTS_ORIGIN } from "@/lib/config";

import { usePreviewHtml, useProfileQuery, useRequestApproval } from "../api";
import { LivePagePreview } from "./LivePagePreview";

/**
 * Final step: preview the assembled showcase exactly as it will render, then
 * request partner/admin approval to publish it.
 */
export function PreviewStep() {
  const { t } = useTranslation();
  const query = usePreviewHtml();
  const profile = useProfileQuery();
  const requestApproval = useRequestApproval();

  // Once published, link to the live Django-hosted vitrine, carrying ?from so
  // that page offers a "back to the Activateur" control (cross-app link).
  const data = profile.data;
  const published = data?.is_published && data?.slug;
  const publishedUrl = published
    ? `${ACCOUNTS_ORIGIN}/communication/${data!.slug}/?from=${encodeURIComponent(
        window.location.href,
      )}`
    : null;

  // Member submit-step state, mirroring communication-preview.html (E-7): the
  // request/publication dates decide pending vs published vs never-submitted;
  // can_submit_approval suppresses the whole submit affordance.
  const canSubmit = data?.can_submit_approval ?? true;
  const requestDate = data?.last_approval_request_date ?? null;
  const publicationDate = data?.last_publication_date ?? null;
  const isPending =
    !!requestDate &&
    (!publicationDate || new Date(requestDate) > new Date(publicationDate));
  const isPublishedState =
    !!publicationDate &&
    (!requestDate || new Date(publicationDate) > new Date(requestDate));

  function submit() {
    requestApproval.mutate(undefined, {
      onSuccess: () => toast.success(t("communications.preview.submitted")),
      // Prefer the refusal the service actually sent, the way ReviewFlow
      // already does for publication. The submission endpoint refuses an
      // account that may not publish with a sentence naming the reason; a
      // generic "try again" tells someone to retry something that will never
      // succeed, and the Django app has always flashed the real one.
      onError: (error) => {
        const detail = (error as unknown as ApiError | undefined)?.detail;
        toast.error(detail ?? t("communications.preview.submitError"));
      },
    });
  }

  const submitButton = (label: string) => (
    <Button onClick={submit} disabled={requestApproval.isPending}>
      {requestApproval.isPending
        ? t("communications.preview.submitting")
        : label}
    </Button>
  );

  const submitArea = () => {
    if (!canSubmit) {
      return (
        <p className="text-sm text-muted-foreground">
          {t("communications.preview.notEligible")}
        </p>
      );
    }
    if (isPending) {
      return (
        <div className="space-y-2">
          <p className="text-sm font-medium text-success">
            {t("communications.preview.pendingNote")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("communications.preview.pendingDelay")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("communications.preview.pendingDisclaimer")}
          </p>
          {submitButton(t("communications.preview.resubmit"))}
        </div>
      );
    }
    if (isPublishedState) {
      return (
        <div className="space-y-2">
          <p className="text-sm">
            {t("communications.preview.publishedOn", {
              date: new Date(publicationDate!).toLocaleDateString(),
            })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("communications.preview.publishedResubmitNote")}
          </p>
          {submitButton(t("communications.preview.resubmit"))}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          {t("communications.preview.neverSubmitted")}
        </p>
        {submitButton(t("communications.preview.submitFirst"))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("communications.preview.title")}</CardTitle>
        <CardDescription>{t("communications.preview.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Approval status + submit + "view published" sit in a bar above the
              preview, so nothing but the wizard's Back/Next follows the preview. */}
          <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border bg-secondary/30 p-4">
            <div className="min-w-0">{submitArea()}</div>
            {publishedUrl && (
              <Button asChild variant="outline">
                <a href={publishedUrl}>
                  <ExternalLink aria-hidden />
                  {t("communications.preview.viewPublished")}
                </a>
              </Button>
            )}
          </div>

          {query.isPending ? (
            <p className="text-muted-foreground">{t("communications.loading")}</p>
          ) : query.isError || !query.data ? (
            <p className="text-destructive">{t("communications.preview.loadError")}</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {t("communications.preview.liveNote")}
              </p>
              <LivePagePreview
                html={query.data}
                title={t("communications.preview.iframeTitle")}
              />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
